import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils/cn';
import type { BrandProfile, BrandColorToken } from '@/lib/api/brand-templates';

/**
 * Brand & report templates editor (T-0112 / C20-122).
 *
 * Mirrors the prototype's `screens-firm.js → firmBrand()` right-hand
 * column: a color-tokens swatch grid + header / footer text editors +
 * cover-layout and type-pairing selectors. The PDF cover preview lives
 * in a sibling component (`pdf-preview-iframe.tsx`); the parent route
 * wires both side-by-side. This component is uncontrolled — it seeds
 * local state from `profile` and emits `onChange` on every edit so the
 * preview can re-render with a small debounce.
 *
 * Per ADR-009 (snapshots are write-once): publishing brand changes does
 * NOT retroactively change captured snapshots. The "Re-render contract"
 * alert at the bottom of the screen surfaces this; the editor itself
 * just sends the new profile to the publish mutation.
 */

export interface BrandTemplateEditorProps {
  profile: BrandProfile;
  onChange: (next: BrandProfile) => void;
}

export function BrandTemplateEditor({ profile, onChange }: BrandTemplateEditorProps) {
  const [draft, setDraft] = useState<BrandProfile>(profile);
  useEffect(() => {
    setDraft(profile);
  }, [profile]);

  const update = useCallback(
    <K extends keyof BrandProfile>(key: K, value: BrandProfile[K]) => {
      setDraft((prev) => {
        const next = { ...prev, [key]: value };
        onChange(next);
        return next;
      });
    },
    [onChange],
  );

  const updateColor = useCallback(
    (id: string, patch: Partial<BrandColorToken>) => {
      setDraft((prev) => {
        const next: BrandProfile = {
          ...prev,
          colors: prev.colors.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        };
        onChange(next);
        return next;
      });
    },
    [onChange],
  );

  return (
    <div className="space-y-4">
      {/* Color tokens */}
      <section
        aria-labelledby="brand-color-tokens-heading"
        className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      >
        <div className="flex items-baseline justify-between">
          <div>
            <h3 id="brand-color-tokens-heading" className="text-h3 font-semibold text-ink-900">
              Color tokens
            </h3>
            <p className="text-meta text-ink-500">
              {draft.colors.length} tokens · exported to PDF stylesheet · used by every chart legend
            </p>
          </div>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {draft.colors.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 rounded-card border border-ink-100 p-2"
            >
              <label
                className="block h-10 w-10 flex-none cursor-pointer overflow-hidden rounded-card border border-ink-100"
                style={{ background: c.hex }}
                aria-label={`Edit ${c.name} hex`}
              >
                <input
                  type="color"
                  value={c.hex}
                  onChange={(e) => updateColor(c.id, { hex: e.target.value })}
                  className="h-full w-full cursor-pointer opacity-0"
                />
              </label>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-body-sm font-semibold text-ink-900">{c.name}</span>
                  <input
                    type="text"
                    value={c.hex.toUpperCase()}
                    onChange={(e) => updateColor(c.id, { hex: e.target.value })}
                    aria-label={`${c.name} hex value`}
                    className="h-6 w-24 rounded-card border border-ink-100 px-1.5 font-mono text-meta text-ink-700 focus:outline-none focus:ring-1 focus:ring-accent-amber-600"
                  />
                </div>
                <div className="text-meta text-ink-500">{c.use}</div>
                <div className="font-mono text-meta text-ink-300">{c.token}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Header / footer / cover */}
      <section
        aria-labelledby="brand-header-footer-heading"
        className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      >
        <h3 id="brand-header-footer-heading" className="text-h3 font-semibold text-ink-900">
          Header &amp; footer
        </h3>
        <p className="text-meta text-ink-500">Appears on every page beyond the cover.</p>

        <div className="mt-3 space-y-3">
          <Field label="Header text" htmlFor="header_text">
            <input
              id="header_text"
              type="text"
              value={draft.header_text}
              onChange={(e) => update('header_text', e.target.value)}
              className={INPUT_CLS}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Footer left" htmlFor="footer_left">
              <input
                id="footer_left"
                type="text"
                value={draft.footer_left}
                onChange={(e) => update('footer_left', e.target.value)}
                className={INPUT_CLS}
              />
              <p className="mt-1 text-meta text-ink-500">
                Variables: <span className="font-mono">{'{snapshot_id}'}</span>,{' '}
                <span className="font-mono">{'{snapshot_date}'}</span>,{' '}
                <span className="font-mono">{'{client}'}</span>,{' '}
                <span className="font-mono">{'{engagement}'}</span>,{' '}
                <span className="font-mono">{'{kind}'}</span> (the report kind, such as
                &ldquo;Vendor intelligence&rdquo;)
              </p>
            </Field>
            <Field label="Footer right" htmlFor="footer_right">
              <input
                id="footer_right"
                type="text"
                value={draft.footer_right}
                onChange={(e) => update('footer_right', e.target.value)}
                className={INPUT_CLS}
              />
              <p className="mt-1 text-meta text-ink-500">
                The same variables, plus page numbering: <span className="font-mono">{'{n}'}</span>,{' '}
                <span className="font-mono">{'{total}'}</span>
              </p>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Cover layout" htmlFor="cover_layout">
              <select
                id="cover_layout"
                value={draft.cover_layout}
                onChange={(e) =>
                  update('cover_layout', e.target.value as BrandProfile['cover_layout'])
                }
                className={INPUT_CLS}
              >
                <option value="full-bleed-band">Full-bleed band (default)</option>
                <option value="minimal-wordmark">Minimal — wordmark + title</option>
                <option value="cover-photo-overlay">Cover photo with overlay</option>
              </select>
            </Field>
            <Field label="Body type pairing" htmlFor="type_pairing">
              <select
                id="type_pairing"
                value={draft.type_pairing}
                onChange={(e) =>
                  update('type_pairing', e.target.value as BrandProfile['type_pairing'])
                }
                className={INPUT_CLS}
              >
                <option value="inter-source-serif">Inter / Source Serif (current)</option>
                <option value="inter-only">Inter only</option>
                <option value="system-ui">System UI fallback</option>
              </select>
            </Field>
          </div>
        </div>
      </section>
    </div>
  );
}

const INPUT_CLS = cn(
  'block h-9 w-full rounded-card border border-ink-200 bg-paper-0 px-2.5 text-body-sm text-ink-900',
  'focus:outline-none focus:ring-2 focus:ring-accent-amber-600',
);

interface FieldProps {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, children }: FieldProps) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1 block text-meta font-medium uppercase tracking-wider text-ink-500"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
